export interface DumbStorageSerializer<T> {
  read: (value: string) => T;
  write: (value: T) => string | null;
}

export interface DumbStorageOptions<T> {
  serializer: DumbStorageSerializer<T>;
}

// Helper to check if we're in browser environment
const isBrowser =
  typeof window !== "undefined" && typeof localStorage !== "undefined";

export interface DumbStorageValueConfig<T> {
  key: string;
  defaultValue: T;
  options: DumbStorageOptions<T>;
  storage?: Storage;
}

export class DumbStorageValue<T> {
  value: T | null = null;
  protected key: string;
  protected defaultValue: T;
  protected options: DumbStorageOptions<T>;
  protected storage: Storage;

  constructor({
    key,
    defaultValue,
    options,
    storage = typeof window !== "undefined" ? localStorage : ({} as Storage),
  }: DumbStorageValueConfig<T>) {
    this.storage = storage;

    this.key = key;

    // Only try to read from storage if we're in browser environment
    if (isBrowser && this.storage.getItem) {
      const storedValue = this.storage.getItem(key);
      this.value = storedValue ? options.serializer.read(storedValue) : null;
    } else {
      this.value = null;
    }

    this.defaultValue = defaultValue;
    this.options = options;
  }

  get(): T {
    if (this.value === null) {
      return this.defaultValue;
    }

    return this.value;
  }

  set(value: T) {
    if (!isBrowser || !this.storage.setItem) {
      this.value = value; // Just update in memory on server
      return;
    }

    const serialized = this.options.serializer.write(value);
    if (serialized === null) {
      this.storage.removeItem(this.key);
    } else {
      this.storage.setItem(this.key, serialized);
    }

    this.value = value;
  }

  remove() {
    if (isBrowser && this.storage.removeItem) {
      this.storage.removeItem(this.key);
    }
    this.value = null;
  }
}

export function jsonOr<T>(value: string, defaultValue: T): T {
  try {
    return JSON.parse(value) as T;
  } catch (_) {
    return defaultValue;
  }
}
